import coordtransform from "coordtransform";
import store from "./store";
import { hasStoredDigg, rememberDigg } from "./digg";
import fetchJsonp from "fetch-jsonp";
import Config from "@/config";
import gpsMock from "@/mock/gps.json";

const closeToast = (role, time) => {
    let _time = time || 1000;
    setTimeout(() => {
        role.toast = false;
        role.tstmsg = null;
    }, _time);
};

const getLocalTime = (dateTimeStamp) => {
    let _time = new Date(dateTimeStamp); // 2018-01-12T21:36:20.202Z =>  UTC时间

    return {
        _date:
            numberfix(_time.getUTCMonth() + 1) +
            "/" +
            numberfix(_time.getUTCDate()),
        _year: _time.getUTCFullYear(),
    };
};

const numberfix = (num) => {
    return Number(num) < 10 ? "0" + num : num;
};

const imgProtocalTeck = (src) => {
    if (!src || typeof src !== "string") return src;
    // 七牛 CDN 已强制 HTTPS。开发页是 HTTP 时若沿用当前协议，会先收到
    // 一个不带 CORS 头的 301，fetch 会在重定向阶段被浏览器拦截。
    if (src.startsWith("//")) return `https:${src}`;
    if (src.startsWith("http://static.eyan.life/")) {
        return src.replace("http://", "https://");
    }
    return src;
};

/**
 * 拼接七牛 CDN 前缀，且**幂等**。
 *
 * 背景：设置页上传成功后存的是 `${config.cdn}${key}` 这种完整地址，
 * 而发布页渲染时又无脑拼了一次 `${config.cdn}${avatar}`，
 * 于是存进库的完整地址被再次加前缀，变成
 * `https://cdn/https://cdn/xxx.png`（重复前缀），图片直接裂掉。
 *
 * 这里做三件事：
 * 1. 逐层剥掉已经存在的七牛 CDN 前缀，顺手救回历史上已经存坏的脏数据，
 *    并沿用输入原本的协议写法；
 * 2. 剥完仍是外部图床地址则原样返回；
 * 3. 其余情况（纯 key / 带 imageView2 参数的 key）正常补前缀。
 */
const cdnUrl = (value = "") => {
    if (!value || typeof value !== "string") return "";
    const raw = value.trim();
    // data: / blob: 是内联或本地预览数据，不走 CDN
    if (/^(data|blob):/i.test(raw)) return raw;

    const cdn = String(Config.cdn || "");
    const host = cdn.replace(/^(https?:)?\/\//i, "").replace(/\/+$/, "");
    if (!host) return raw;
    // 纯 key 补前缀时沿用 config.cdn 本来的协议写法。
    // 注意捕获组只拿到 "https:"，"//" 要自己补，否则会拼成 https:host/...。
    const cdnScheme = /^https?:/i.test(cdn)
        ? `${cdn.match(/^(https?:)/i)[1]}//`
        : "//";

    // 循环剥掉重复前缀：cdn + cdn + key => key。
    // 每层前缀的协议可有可无（重复拼接时第二层往往就只剩域名了），
    // 所以协议用可选匹配。
    //
    // 判定「这一层是 CDN 前缀」不能只比对当前 config.cdn 的域名：
    // 开发环境 VITE_CDN 指向测试桶 static.test.eyan.life，
    // 而库里可能存着生产桶 static.eyan.life 拼出来的脏数据，
    // 只认当前域名会让这种地址原样漏出去（等于没修）。
    // 所以显式兼容当前、生产和测试 CDN；不能按根域名泛化，否则会误把
    // api.eyan.life、m.eyan.life 等非媒体地址当作 CDN 前缀剥掉。
    const hostLower = host.toLowerCase();
    const ownCdnHosts = new Set([
        hostLower.split(":")[0],
        "static.eyan.life",
        "static.test.eyan.life",
    ]);
    const isOwnCdn = (candidate = "") => {
        const c = candidate.toLowerCase().split(":")[0];
        return ownCdnHosts.has(c);
    };

    let rest = raw;
    let scheme = "";
    let resolvedHost = host;
    let guard = 0;
    while (guard < 5) {
        const m = rest.match(/^(?:(https?:)?\/\/)?([^/]+)(?:\/|$)/i);
        if (!m) break;
        // 不是自家 CDN 就停下，别去动外部图床的地址
        if (!isOwnCdn(m[2])) break;
        // 记住最初那层前缀的协议写法，输出时保持一致。
        // 协议相对（无捕获组）就保持 "//"，不借用 config.cdn 的协议。
        if (!scheme) {
            scheme = m[1] ? `${m[1]}//` : "//";
            resolvedHost = m[2];
        }
        rest = rest.slice(m[0].length).replace(/^\/+/, "");
        guard += 1;
    }

    // 剥完还剩绝对地址（外部图床），原样返回
    if (/^(https?:)?\/\//i.test(rest)) return rest;
    if (!rest) return "";

    return `${scheme || cdnScheme}${resolvedHost}/${rest.replace(/^\/+/, "")}`;
};

const getDateDiff = (dateTimeStamp) => {
    let minute = 1000 * 60;
    let hour = minute * 60;
    let day = hour * 24;
    // let halfamonth = day * 15;
    let month = day * 30;
    let now = new Date().getTime();
    let diffValue = now - dateTimeStamp;
    if (diffValue < 0) return;

    let monthC = diffValue / month;
    let weekC = diffValue / (7 * day);
    let dayC = diffValue / day;
    let hourC = diffValue / hour;
    let minC = diffValue / minute;
    let result = "";
    if (monthC >= 1) {
        result += parseInt(monthC) + "月前";
    } else if (weekC >= 1) {
        result += parseInt(weekC) + "周前";
    } else if (dayC >= 1) {
        result += parseInt(dayC) + "天前";
    } else if (hourC >= 1) {
        result += parseInt(hourC) + "小时前";
    } else if (minC >= 1) {
        result += parseInt(minC) + "分钟前";
    } else result = "刚刚";
    return result;
};

const requireScript = (src) => {
    return new Promise((resolve, reject) => {
        try {
            if (!src) {
                reject(new Error("src地址为空"));
                return;
            }
            const existingScript = document.getElementById(src);
            if (existingScript?.dataset.loaded === "true") {
                resolve();
                return;
            }

            const script = existingScript || document.createElement("script");
            const handleLoad = () => {
                script.dataset.loaded = "true";
                resolve();
            };
            const handleError = () => {
                script.remove();
                reject(new Error(`${src} 加载失败`));
            };

            script.addEventListener("load", handleLoad, { once: true });
            script.addEventListener("error", handleError, { once: true });
            if (existingScript) return;

            script.src = src;
            script.async = true;
            script.id = src;
            document.body.appendChild(script);
        } catch (err) {
            console.error(err);
            reject(err);
        }
    });
};

const isIphonex = () => {
    // X XS, XS Max, XR
    const xSeriesConfig = [
        {
            devicePixelRatio: 3,
            width: 375,
            height: 812,
        },
        {
            devicePixelRatio: 3,
            width: 414,
            height: 896,
        },
        {
            devicePixelRatio: 2,
            width: 414,
            height: 896,
        },
    ];
    // h5
    if (typeof window !== "undefined" && window) {
        const isIOS = /iphone/gi.test(window.navigator.userAgent);
        if (!isIOS) return false;
        const { devicePixelRatio, screen } = window;
        const { width, height } = screen;
        return xSeriesConfig.some(
            (item) =>
                item.devicePixelRatio === devicePixelRatio &&
                item.width === width &&
                item.height === height,
        );
    }
    return false;
};

/**
 * 七牛bucket接口，根据存储内容获取exif信息
 * @param {String} url 七牛地址，只能是媒体
 */
const getExif = (url) => {
    return new Promise((resolve, reject) => {
        fetch(`${imgProtocalTeck(url)}?exif`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`EXIF 请求失败（${response.status}）`);
                }
                return response.json();
            })
            .then((data) => {
                resolve(data);
            })
            .catch((error) => {
                reject(error);
                console.error("Error:", error);
            });
    });
};

/**
 * 七牛bucket接口，根据存储内容获取图片颜色平均值
 * https://developer.qiniu.com/dora/1268/image-average-hue-imageave
 * @param {String} url 七牛地址，只能是图片
 */
const getImageAve = (url) => {
    return new Promise((resolve, reject) => {
        fetch(`${imgProtocalTeck(url)}?imageAve`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`图片颜色请求失败（${response.status}）`);
                }
                return response.json();
            })
            .then((data) => {
                if (data.error) {
                    data.RGB = null;
                } else {
                    data.code = 200;
                    data.RGB = `#${data.RGB.slice(2)}`;
                }
                resolve(data);
            })
            .catch((error) => {
                reject(error);
                console.error("Error:", error);
            });
    });
};

/**
 * 转换七牛kodo的gps坐标
 */
const tfCoord = (data) => {
    let olat = data.GPSLatitude.val.split(", ");
    let olng = data.GPSLongitude.val.split(", ");
    let lat = 0,
        lng = 0;
    for (let i = 0; i < olat.length; i++) {
        lat += olat[i] / Math.pow(60, i);
        lng += olng[i] / Math.pow(60, i);
    }
    lat = data.GPSLatitudeRef.val == "S" ? -lat : lat;
    lng = data.GPSLongitudeRef.val == "W" ? -lng : lng;
    return coordtransform.wgs84togcj02(lng, lat);
};

/**
 * 根据度分秒转换成经纬度
 * 坐标是以度为单位的，东经北纬为正值，西经南纬为负值。这里需要将度分秒的经纬度转换以度为单位的，因度分秒之间为 60 进制，故转换公式为：
 * 度分秒 = 度 + 分 / 60 + 秒 / (60 * 60)
 * @param {int} degree 度
 * @param {int} minute 分
 * @param {int} second 秒
 */
const getCoord = (degree, minute, second) => {
    return degree + minute / 60 + second / (60 * 60);
};

/**
 * 根据坐标转换成详细地址
 * 腾讯地图sdk https://lbs.qq.com/service/webService/webServiceGuide/webServiceGcoder
 * ex: https://apis.map.qq.com/ws/geocoder/v1/?output=jsonp&location=23.290412836766404,113.30051091651734&get_poi=1&key=7QJBZ-N4N6J-ALPFH-FRUPA-MOAQO-XAFTX
 * @param {int} longitude 经度
 * @param {int} latitude 纬度
 */
const getAddressByCoord = (longitude, latitude) => {
    return new Promise((resolve, reject) => {
        if (import.meta.env.DEV) {
            console.warn("不支持调试白名单，MOCK GPS地理位置信息");
            return resolve(gpsMock);
        }
        fetchJsonp(
            `https://apis.map.qq.com/ws/geocoder/v1/?output=jsonp&location=${latitude},${longitude}&get_poi=1&key=${Config.qqMapKey}`,
            {},
        )
            .then((response) => response.json())
            .then((data) => {
                resolve(data);
            })
            .catch((error) => {
                reject(error);
                console.error("Error:", error);
            });
    });
};

/**
 * 获取url参数
 * @param {String} string 字符串/url
 */
const getURLParameters = (string) => {
    return (
        string
            .match(/([^?=&]+)(=([^&]*))/g)
            // eslint-disable-next-line
            .reduce(
                (array, value) => (
                    (array[value.slice(0, value.indexOf("="))] = value.slice(
                        value.indexOf("=") + 1,
                        // eslint-disable-next-line
                    )),
                    array
                ),
                {},
            )
    );
};

/**
 * 判断字符串返回是不是对象，AVOS存储一些字段是JSON.stringify进去的
 */
const isJsonString = (str) => {
    if (typeof str == "string") {
        try {
            const obj = JSON.parse(str);
            if (typeof obj == "object" && obj) {
                return true;
            } else {
                return false;
            }
        } catch (e) {
            return false;
        }
    }
    return false;
};

/**
 * 获取文件缩略图，如果是input，就是files[0]
 */
/**
 * 安全获取宝宝（watcher）信息
 * 接口 /get/?info=system 返回的数据里并不包含 category 字段，
 * 早期直接读system.category[baby].avatar 会在字段缺失时抛错，
 * 导致整棵React 树被卸载、页面白屏。这里统一做安全兜底。
 * @param {Object} system 接口返回的 system 对象
 * @param {Number|String} baby 宝宝序号
 * @returns {Object} 至少包含 avatar / name，缺失时为空字符串
 */
const getWatcher = (system, baby) => {
    const cate = system && system.category && system.category[baby];
    return {
        no: Number(baby),
        avatar: (cate && cate.avatar) || "",
        name: (cate && cate.name) || `孩子 ${baby}`,
    };
};

const getWatchers = (system, babyNos, baby) => {
    const source = Array.isArray(babyNos) && babyNos.length ? babyNos : [baby];
    return [...new Set(source.map(Number).filter(Boolean))].map((no) =>
        getWatcher(system, no),
    );
};

const fileToThumb = (file) => {
    const a = new FileReader();
    a.onload = function (e) {
        var base64Str = e.target.result; //获取base64
        var base64Code = base64Str.substring(base64Str.indexOf(",") + 1);

        return base64Code;
    };
    a.readAsDataURL(file);
};

export {
    store,
    getDateDiff,
    imgProtocalTeck,
    cdnUrl,
    numberfix,
    getLocalTime,
    closeToast,
    requireScript,
    hasStoredDigg,
    rememberDigg,
    isIphonex,
    getURLParameters,
    getExif,
    getImageAve,
    getCoord,
    getAddressByCoord,
    tfCoord,
    isJsonString,
    fileToThumb,
    getWatcher,
    getWatchers,
};
